import React from 'react';
import { 
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ComposedChart 
} from 'recharts';

const TelehealthSection = () => (
  <div className="space-y-6">
    <div className="text-center mb-6">
      <h2 className="text-3xl font-bold text-gray-900">Telehealth Innovation</h2>
      <p className="text-gray-600 mt-2">Expanding access through digital health solutions</p>
    </div>

    {/* ADHD Telehealth vs Office Visits Chart */}
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-xl font-semibold mb-2">ADHD Telehealth vs Office Visits Distribution</h3>
      <p className="text-sm text-gray-600 mb-4">Showing the relative proportion of telehealth to office visits for ADHD diagnoses over time</p>
      <ResponsiveContainer width="100%" height={350}>
        <AreaChart data={[
          { year: '2021', telehealth: 248, office: 1534, total: 1782 },
          { year: '2022', telehealth: 302, office: 1654, total: 1956 },
          { year: '2023', telehealth: 386, office: 2049, total: 2435 },
          { year: '2024', telehealth: 391, office: 2181, total: 2572 },
          { year: 'Jan-May 2025', telehealth: 163, office: 1127, total: 1290 }
        ]}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis />
          <Tooltip formatter={(value) => value.toLocaleString()} />
          <Legend />
          <Area
            type="monotone"
            dataKey="telehealth"
            stackId="1"
            stroke="#8884d8"
            fill="#8884d8"
            name="Telehealth Visits"
          />
          <Area
            type="monotone"
            dataKey="office"
            stackId="1"
            stroke="#82ca9d"
            fill="#82ca9d"
            name="Office Visits"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>

    {/* General Pediatric and ADHD Comparison */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-xl font-semibold mb-2">General Pediatric Telehealth vs Office Visits</h3>
        <p className="text-sm text-gray-600 mb-4">Total general pediatric visits showing telehealth adoption across all pediatric services</p>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={[
            { year: '2021', telehealth: 1505, office: 56017, total: 57522 },
            { year: '2022', telehealth: 2006, office: 63958, total: 65964 },
            { year: '2023', telehealth: 2119, office: 67933, total: 70052 },
            { year: '2024', telehealth: 1870, office: 74479, total: 76349 },
            { year: 'Jan-May 2025', telehealth: 804, office: 36199, total: 37003 }
          ]}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis />
            <Tooltip 
              formatter={(value, name) => [value?.toLocaleString(), name]}
              labelFormatter={(label) => `Year: ${label}`}
            />
            <Legend />
            <Area
              dataKey="office"
              fill="#82ca9d"
              name="Office Visits"
              opacity={0.7}
            />
            <Line
              type="monotone"
              dataKey="telehealth"
              stroke="#3B82F6"
              strokeWidth={4}
              name="Telehealth Visits"
              dot={{ fill: '#3B82F6', strokeWidth: 2, r: 6 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
        
        {/* ADHD vs General Pediatric Telehealth Adoption Rates */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="text-lg font-medium mb-2">ADHD vs General Pediatric Telehealth Adoption Rates</h4>
          <p className="text-sm text-gray-600 mb-3">Comparing telehealth adoption rates between ADHD patients and general pediatric population</p>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={[
              { 
                year: '2021', 
                adhdTelehealthRate: 13.92,
                generalTelehealthRate: 2.62,
                adhdTotal: 1782,
                generalTotal: 57522
              },
              { 
                year: '2022', 
                adhdTelehealthRate: 15.44,
                generalTelehealthRate: 3.04,
                adhdTotal: 1956,
                generalTotal: 65964
              },
              { 
                year: '2023', 
                adhdTelehealthRate: 15.85,
                generalTelehealthRate: 3.02,
                adhdTotal: 2435,
                generalTotal: 70052
              },
              { 
                year: '2024', 
                adhdTelehealthRate: 15.20,
                generalTelehealthRate: 2.45,
                adhdTotal: 2572,
                generalTotal: 76349
              },
              { 
                year: 'Jan-May 2025', 
                adhdTelehealthRate: 12.64,
                generalTelehealthRate: 2.17,
                adhdTotal: 1290,
                generalTotal: 37003
              }
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis domain={[0, 20]} />
              <Tooltip 
                formatter={(value, name, props) => {
                  if (name === 'ADHD Telehealth Rate') {
                    return [`${value}% (${Math.round(value * props.payload.adhdTotal / 100)} visits out of ${props.payload.adhdTotal.toLocaleString()})`, name];
                  }
                  if (name === 'General Pediatric Telehealth Rate') {
                    return [`${value}% (${Math.round(value * props.payload.generalTotal / 100).toLocaleString()} visits out of ${props.payload.generalTotal.toLocaleString()})`, name];
                  }
                  return [value, name];
                }}
                labelFormatter={(label) => `Year: ${label}`}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="adhdTelehealthRate"
                stroke="#8b5cf6"
                strokeWidth={4}
                name="ADHD Telehealth Rate"
                dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="generalTelehealthRate"
                stroke="#F59E0B"
                strokeWidth={3}
                name="General Pediatric Telehealth Rate"
                dot={{ fill: '#F59E0B', strokeWidth: 2, r: 5 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-3 p-3 bg-purple-50 rounded-lg">
            <p className="text-sm text-purple-800">
              <strong>Key Insight:</strong> ADHD patients show consistently higher telehealth adoption rates (12-16%) 
              compared to general pediatric patients (2-3%), indicating stronger suitability for virtual care in mental health services.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-orange-50 p-6 rounded-lg">
        <h3 className="text-lg font-bold mb-2 text-orange-900">Current Telehealth Benefits</h3>
        <ul className="list-disc pl-6 text-orange-800 space-y-2">
          <li>Improved access for rural populations</li>
          <li>Reduced travel time and costs</li>
          <li>Enhanced continuity of care</li>
          <li>Better patient engagement</li>
          <li>Reduced no-show rates</li>
          <li>Flexible scheduling options</li>
        </ul>
        
        <div className="mt-4 p-4 bg-white rounded-lg border border-orange-200">
          <h4 className="font-semibold text-orange-900 mb-2">ADHD-Specific Telehealth Advantages</h4>
          <p className="text-sm text-orange-800 mb-3">
            <strong>4-5x Higher Adoption Rate:</strong> ADHD patients show 12-16% telehealth usage vs. 2-3% for general pediatrics, indicating exceptional suitability for mental health services.
          </p>
          <ul className="text-sm text-orange-700 space-y-1">
            <li>• <strong>Reduced stigma:</strong> Home environment decreases barriers to seeking mental health care</li>
            <li>• <strong>Familiar setting:</strong> Children and adolescents feel more comfortable in their own space</li>
            <li>• <strong>Medication monitoring:</strong> Frequent ADHD follow-ups are ideal for virtual visits</li>
            <li>• <strong>Parent involvement:</strong> Easier family participation in treatment planning</li>
            <li>• <strong>Consistency:</strong> Maintains therapeutic relationships during school/work conflicts</li>
            <li>• <strong>Crisis support:</strong> Immediate access during behavioral episodes or medication adjustments</li>
          </ul>
        </div>
      </div>
    </div>

    {/* Telehealth Expansion Opportunity */}
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-2xl font-bold text-gray-900 mb-4">Significant Scope for Telehealth Expansion</h3>
      <p className="text-gray-600 mb-6">
        Despite steady growth, telehealth represents only <strong>2.8% of total visits</strong> in 2024, 
        indicating tremendous untapped potential for expanding access to pediatric mental health services.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-blue-900">Why Telehealth Expansion is Critical</h4>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h5 className="font-medium text-blue-800 mb-2">Access Barriers</h5>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Geographic isolation in rural areas</li>
              <li>• Transportation challenges for families</li>
              <li>• Limited pediatric mental health specialists</li>
              <li>• Long wait times for appointments</li>
              <li>• Parent work schedule conflicts</li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h5 className="font-medium text-green-800 mb-2">Mental Health Specific Benefits</h5>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• Reduced stigma of seeking mental health care</li>
              <li>• Comfortable home environment for adolescents</li>
              <li>• Easier follow-up for medication management</li>
              <li>• Better family involvement in sessions</li>
              <li>• Continuity during school/work schedules</li>
            </ul>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-purple-900">Expansion Opportunities</h4>
          <div className="bg-purple-50 p-4 rounded-lg">
            <h5 className="font-medium text-purple-800 mb-2">ADHD & Mental Health Focus</h5>
            <ul className="text-sm text-purple-700 space-y-1">
              <li>• Routine ADHD follow-ups and medication monitoring</li>
              <li>• Anxiety and depression screening sessions</li>
              <li>• Parent education and support groups</li>
              <li>• Collaborative care with primary providers</li>
              <li>• Crisis intervention and urgent consultations</li>
            </ul>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h5 className="font-medium text-yellow-800 mb-2">Scalability Potential</h5>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Could serve 10x more patients with same resources</li>
              <li>• Reduced overhead costs per visit</li>
              <li>• Ability to reach underserved communities</li>
              <li>• Integration with school-based programs</li>
              <li>• 24/7 crisis consultation capabilities</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
        <h5 className="font-semibold text-blue-900 mb-2">Projected Impact of Telehealth Expansion</h5>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="text-center p-3 bg-white rounded-lg">
            <div className="text-2xl font-bold text-blue-600">15-20%</div>
            <div className="text-gray-600">Target telehealth percentage</div>
            <div className="text-xs text-gray-500 mt-1">vs. current 2.8%</div>
          </div>
          <div className="text-center p-3 bg-white rounded-lg">
            <div className="text-2xl font-bold text-green-600">5,000+</div>
            <div className="text-gray-600">Additional annual visits</div>
            <div className="text-xs text-gray-500 mt-1">Potential capacity increase</div>
          </div>
          <div className="text-center p-3 bg-white rounded-lg">
            <div className="text-2xl font-bold text-purple-600">50%</div>
            <div className="text-gray-600">Reduction in wait times</div>
            <div className="text-xs text-gray-500 mt-1">For mental health services</div>
          </div>
        </div>
      </div>
    </div>

    {/* Telehealth Expansion Strategy */}
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-xl font-semibold text-gray-900 mb-4">Telehealth Expansion Strategy</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-medium text-gray-800 mb-3">Phase 1: Infrastructure (3-6 months)</h4>
          <ul className="text-sm text-gray-700 space-y-2">
            <li>• Upgrade telehealth platforms for pediatric use</li>
            <li>• Train staff on virtual care protocols</li>
            <li>• Establish quality assurance measures</li>
            <li>• Develop patient onboarding processes</li>
          </ul>
        </div>
        <div>
          <h4 className="font-medium text-gray-800 mb-3">Phase 2: Service Expansion (6-12 months)</h4>
          <ul className="text-sm text-gray-700 space-y-2">
            <li>• Launch ADHD telehealth follow-up programs</li>
            <li>• Implement virtual group therapy sessions</li>
            <li>• Expand to rural and underserved areas</li>
            <li>• Integrate with school-based health programs</li>
          </ul>
        </div>
      </div>
    </div>

    <div className="mt-8 p-4 border-l-4 border-orange-200 bg-orange-50">
      <strong>Digital Health Success:</strong> 42% growth in telehealth adoption demonstrates program's adaptability and patient-centered approach. 
      <strong>Expansion Opportunity:</strong> With only 2.8% of visits currently via telehealth, there's potential to reach 15-20% through targeted expansion, 
      dramatically improving access to mental health services for pediatric patients.
    </div>
  </div>
);

export default TelehealthSection;
